import { lazy, Suspense, useMemo, useState } from 'react'
import { aUnPoint, CENTRES_QUARTIERS, intituleAdresse, LIBELLE_SOURCE, saisieDepuisConnue, type AdresseConnue, type AdresseSaisie } from '../api/adresses'
import { encoder, lirePlusCode } from '../api/pluscode'
import { dansBoiteMali } from '../api/geo'
import { QUARTIERS } from './Format'
import { Icone } from './Icone'
import { ListeDeroulante } from './ListeDeroulante'
import type { Point } from './ChoixPosition'

/* MapLibre pèse près d'un mégaoctet : la carte est chargée À LA DEMANDE (forfaits mobiles de Bamako,
   et la prise d'appel doit s'afficher tout de suite). */
const ChoixPosition = lazy(() => import('./ChoixPosition').then((m) => ({ default: m.ChoixPosition })))

const OPTIONS_QUARTIER = [{ valeur: '', libelle: 'Recentrer la carte sur un quartier…' }, ...QUARTIERS.map((q) => ({ valeur: q, libelle: q }))]

/**
 * Une extrémité du trajet (collecte OU livraison), saisie en quatre gestes complémentaires :
 *   1. une adresse DÉJÀ UTILISÉE (pastilles) ;
 *   2. l'épingle sur la carte (OpenFreeMap) — le quartier ne fait que RECENTRER la carte ;
 *   3. un Plus Code lu par le client (complet, ou court = relatif au centre de Bamako) ;
 *   4. le texte de l'adresse et un REPÈRE pour le livreur.
 * Le point (latitude/longitude) est ce qui compte : c'est lui qui permet à la base de calculer la
 * distance, donc le prix.
 */
export function SelecteurAdresse({
  titre,
  valeur,
  surChange,
  connues = [],
  libelleConnues,
  placeholderTexte,
  placeholderRepere,
}: {
  titre: string
  valeur: AdresseSaisie
  surChange: (a: AdresseSaisie) => void
  connues?: AdresseConnue[]
  libelleConnues: string
  placeholderTexte: string
  placeholderRepere: string
}) {
  const [plusCode, setPlusCode] = useState('')
  const [erreurPlusCode, setErreurPlusCode] = useState<string | null>(null)
  const [centre, setCentre] = useState<Point | null>(null)
  const [quartier, setQuartier] = useState('')

  const point: Point | null = aUnPoint(valeur) ? [valeur.lat, valeur.lng] : null
  const codeAffiche = useMemo(() => (point ? encoder(point[0], point[1]) : null), [point])

  function placerPlusCode() {
    const r = lirePlusCode(plusCode)
    if (!r.ok) {
      setErreurPlusCode(r.raison)
      return
    }
    if (!dansBoiteMali(r.lat, r.lng)) {
      setErreurPlusCode(
        r.court
          ? "Ce code court tombe hors du Mali : demandez le code COMPLET (avec les 4 premiers caractères)."
          : "Ce Plus Code est situé hors du Mali : vérifiez-le avec le client.",
      )
      return
    }
    setErreurPlusCode(null)
    surChange({ ...valeur, lat: r.lat, lng: r.lng, source: 'plus_code' })
    setPlusCode('')
  }

  return (
    <div className="bloc-adresse">
      <h4>
        <Icone nom="pin" classe="h-ic" /> {titre}
      </h4>

      {connues.length > 0 && (
        <div>
          <div className="t-muted" style={{ fontSize: 12, marginBottom: 6 }}>
            {libelleConnues}
          </div>
          <div className="puces-adresses">
            {connues.map((a) => (
              <button
                key={a.id}
                type="button"
                className="puce-adresse"
                title={`${LIBELLE_SOURCE[a.source]} · utilisée ${a.use_count} fois`}
                onClick={() => {
                  surChange(saisieDepuisConnue(a))
                  setErreurPlusCode(null)
                }}
              >
                {intituleAdresse(a)}
                <small>×{a.use_count}</small>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ligne">
        <div className="field">
          <label>Quartier (recentre seulement la carte)</label>
          <ListeDeroulante
            valeur={quartier}
            options={OPTIONS_QUARTIER}
            pleineLargeur
            surChangement={(q) => {
              setQuartier(q)
              const c = CENTRES_QUARTIERS[q]
              if (c) setCentre([c[0], c[1]])
            }}
          />
        </div>
        <div className="field">
          <label htmlFor={`pc-${titre}`}>Plus Code (donné par le client)</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              id={`pc-${titre}`}
              placeholder="ex. 9QCJ+2VX ou 7FG49QCJ+2VX"
              autoComplete="off"
              value={plusCode}
              aria-invalid={erreurPlusCode ? true : undefined}
              onChange={(e) => {
                setPlusCode(e.target.value)
                setErreurPlusCode(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  placerPlusCode()
                }
              }}
            />
            <button type="button" className="btn btn-outline btn-sm" style={{ width: 'auto' }} onClick={placerPlusCode} disabled={!plusCode.trim()}>
              Placer
            </button>
          </div>
        </div>
      </div>
      {erreurPlusCode && (
        <div className="bandeau erreur" role="alert" style={{ margin: 0 }}>
          {erreurPlusCode}
        </div>
      )}

      <Suspense fallback={<div className="coque-carte squelette" style={{ height: 240 }} aria-busy="true" aria-label="Chargement de la carte" />}>
        <ChoixPosition
          position={point}
          centre={centre}
          surChoix={(p) => surChange({ ...valeur, lat: p[0], lng: p[1], source: 'epingle' })}
          etiquette={`Carte : ${titre}`}
        />
      </Suspense>

      <div className={`etat-point${point ? ' ok' : ''}`} aria-live="polite">
        {point ? (
          <>
            <Icone nom="check" /> Point placé :<span className="mono">{point[0].toFixed(5)} ; {point[1].toFixed(5)}</span>
            {codeAffiche && <span>· Plus Code {codeAffiche}</span>}
            <button type="button" className="btn btn-ghost btn-sm" style={{ width: 'auto' }} onClick={() => surChange({ ...valeur, lat: null, lng: null, source: null })}>
              Retirer le point
            </button>
          </>
        ) : (
          <>Aucun point : posez l’épingle sur la carte, ou saisissez un Plus Code. Sans point, le prix ne peut pas être calculé.</>
        )}
      </div>

      <div className="ligne">
        <div className="field">
          <label>Adresse (en clair)</label>
          <input placeholder={placeholderTexte} value={valeur.texte} maxLength={300} onChange={(e) => surChange({ ...valeur, texte: e.target.value })} />
        </div>
        <div className="field">
          <label>Repère pour le livreur</label>
          <input placeholder={placeholderRepere} value={valeur.repere} maxLength={200} onChange={(e) => surChange({ ...valeur, repere: e.target.value })} />
        </div>
      </div>
    </div>
  )
}
